import ElitePeersList from './ElitePeersList'
import { useQuery } from '@tanstack/react-query'
import { fetchEliteScoreLeaderboard } from '../../../repository/MutualFundDetail'
import { useEffect, useState } from 'react'
import {
  EliteScoreLeaderboardResponseType,
  EliteScoresTypes,
  WaterfallSections
} from '../../../types/mutual-fund-detail.types'
import { opacityElementByAttribute, toPascalCaseWithSpaces } from '../../../utils/utils'
import { EventNames } from '../../../types/misc.types'
import { PeerListSkeletonLoader } from '../../skeletons/PeerListSkeleton'
import PeersSheet from '../../peers/PeersSheet'
import { trackEvent } from '../../../utils/appBridge'
import useAlternateISIN from '../../../hooks/useAlternateISIN'
import { getAppVersion } from '../../../utils/sessionStorage'
import { useEliteBuyCtaExtras } from '../EliteBuyCtaExtrasContext'
interface Fund {
  isin: string
  fundName: string
  score: number
  rank: number
}

function sortFundsByRank(funds: Fund[]) {
  // Create a copy of the array to avoid modifying the original
  const sortedFunds = [...funds]

  // Sort the array based on the rank property
  sortedFunds?.sort((a, b) => {
    return (a?.rank || Infinity) - (b?.rank || Infinity)
  })

  return sortedFunds
}

export default function EliteScoreLeaderboardSection({
  eliteScoresType,
  fundCategory
}: {
  eliteScoresType: `${EliteScoresTypes}`
  fundCategory: string
}) {
  const [peerData, setPeerData] = useState<Fund[]>([])
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()

  const { alternateISIN } = useAlternateISIN(WaterfallSections.ELITE)

  const ScreenName = `MF_L2_Elite_${toPascalCaseWithSpaces(eliteScoresType)}_Peer`

  const { data, isLoading, isError } = useQuery<any, Error>({
    queryKey: ['eliteScoreLeaderboard', eliteScoresType, alternateISIN, fundCategory],
    queryFn: () => {
      return fetchEliteScoreLeaderboard(eliteScoresType, fundCategory)
    },
    enabled: !!alternateISIN,
    refetchOnWindowFocus: false,
    retry: 1
  })

  useEffect(() => {
    if (data) {
      const dataCopy = data?.data as EliteScoreLeaderboardResponseType
      setPeerData(() => sortFundsByRank(dataCopy?.powerScorePeerList) as Fund[])
    }
  }, [data])

  const getRank = () => {
    return peerData?.filter((peerDatum) => peerDatum?.isin === alternateISIN)?.[0]?.rank ?? 0
  }

  const openBottomSheet = () => {
    setIsSheetOpen(true)
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...eliteBuyCtaExtras,
        eventId: 'View All',
        ScreenName,
        fundName: alternateISIN,
        screenType: 'MF_L2'
      }
    })
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '1')
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ...eliteBuyCtaExtras,
        eventId: 'Peer',
        ScreenName,
        fundName: alternateISIN,
        screenType: 'MF_L2'
      }
    })
  }, [])

  const closeBottomSheet = () => {
    setIsSheetOpen(false)
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '0')
  }

  const getScoreDisplayName = (scoreType: `${EliteScoresTypes}`) => {
    const appVersion = getAppVersion()

    switch (scoreType) {
      case EliteScoresTypes.CONSISTENCY:
        return appVersion >= 47 ? 'Consistency Score' : 'Returns Score'
      case EliteScoresTypes.VOLATILITY:
        return appVersion >= 47 ? 'Volatility Score' : 'Stability Score'
      case EliteScoresTypes.RECENCY:
        return 'Recency Score'
      default:
        return toPascalCaseWithSpaces(scoreType) + ' Score'
    }
  }

  if (isLoading) return <PeerListSkeletonLoader />

  return (
    <div className='flex flex-col w-full'>
      <div className={`text-black text-[14px] font-[500] pb-1 `}>Rank in {toPascalCaseWithSpaces(fundCategory)}</div>
      <div className={`flex gap-[4px] text-[#3D3D3D] pb-5 text-[14px] `}>
        {isError || peerData?.length <= 0 ? (
          <div className='text-[#8E9393] text-13 font-[700]'>Not Available</div>
        ) : (
          <div>
            Based on <span className='text-[#CC7A29] font-[500]'> {getScoreDisplayName(eliteScoresType)} </span>
          </div>
        )}
        <div></div>
      </div>
      <hr />
      <div className={`${peerData?.length > 0 ? 'pb-6' : 'pb-10'}  `}>
        {peerData?.length > 0 ? (
          <ElitePeersList
            data={peerData}
            isinToBeHighlighted={alternateISIN}
            rank={getRank()}
            isRenderAllRows={false}
            onViewAll={openBottomSheet}
            scoresType={eliteScoresType}
          />
        ) : (
          <ElitePeersList
            data={Array(5)
              .fill(0)
              ?.map((_datum, idx) => {
                return {
                  rank: idx + 1,
                  fundName: idx === 2 ? 'Peer List Unavailable' : '-',
                  score: 0,
                  isin: idx === 2 ? alternateISIN : ''
                }
              })}
            rank={3}
            isinToBeHighlighted={alternateISIN}
            isRenderAllRows={false}
            isError={true}
            scoresType={eliteScoresType}
          />
        )}
        <PeersSheet
          header={
            <div className='flex flex-col text-left pb-2'>
              <div className={`text-black text-[14px] font-[500] pb-1 px-1`}>
                Rank in {toPascalCaseWithSpaces(fundCategory)}
              </div>
              <div className={`flex gap-[4px] text-[#3D3D3D]  text-[14px] px-1`}>
                <div>
                  Based on <span className='text-[#CC7A29] font-[500]'> {getScoreDisplayName(eliteScoresType)} </span>
                </div>
                <div></div>
              </div>
            </div>
          }
          isOpen={isSheetOpen}
          onClose={closeBottomSheet}
        >
          <div className='w-full pl-4 pr-6 pb-1'>
            <ElitePeersList
              data={peerData}
              rank={getRank()}
              isinToBeHighlighted={alternateISIN}
              isRenderAllRows={true}
              scoresType={eliteScoresType}
            />
          </div>
        </PeersSheet>
      </div>
    </div>
  )
}
